














































import { Component, Vue, Watch } from 'vue-property-decorator';
import FloatingBottomContainer from '@/components/FloatingBottomContainer.vue';
import MelosPlayer from '@/components/MelosPlayer.vue';
import MelosPlayerInterface from '@/components/MelosPlayerInterface.vue';
import PlayerData from '@/store';
import MelosButton from '@/components/MelosButton.vue';
import { StationStatus, MelosStation } from '@/services/api.service';
import { MelosPlayerStatus } from '@/components/MelosPlayer.vue';
import _ from 'lodash';

@Component({
  components: {
    FloatingBottomContainer,
    MelosPlayer,
    MelosPlayerInterface,
    MelosButton,
  },
})
export default class Main extends Vue {
  private defaultView: string = 'home';
  private loggedIn: boolean = false;
  private debouncedFetchStationData = _.debounce(this.fetchStationData, 2000);
  private lastSelectedStation = '';

  private async mounted() {
    if (this.activeView === 'mainView') {
      this.activeView = this.defaultView;
    }
    return this.initStations();
  }

  @Watch('stationUriTime')
  @Watch('ownedStations')
  @Watch('ownedStations')
  @Watch('token')
  private async onSessionStateChanged() {
    return this.initStations();
  }

  private async initStations() {
    if (!!this.$sessionService.getSessionData() &&
    this.$sessionService.getSessionData().token !== '') {
      this.loggedIn = true;
      await this.fetchStations();
    } else {
      this.loggedIn = false;
      await this.fetchPublicStations();
    }
  }

  private get ownedStations(): MelosStation[] {
    return this.$store.state.ownedStations || [];
  }

  private get stationUriTime() {
    return this.$store.state.stationUriTime;
  }

  private get onUserDetailsView(): boolean {
    return this.activeView === 'userDetails';
  }

  private get imageSrc(): string | undefined {
    return this.$store.state.player?.imageSrc;
  }

  private get activeView(): string {
    return this.$route.name || '';
  }

  private set activeView(tabId: string) {
    this.$router.push({ path: tabId });
  }

  private get token(): string {
    return this.$store.state.session?.token;
  }

  private get audioSource(): string | undefined {
    return this.$store.state.player?.audioSource;
  }

  private get playerData(): any {
    return this.$store.state.player;
  }

  private get stationId(): string | undefined {
    return this.$store.state.player?.stationId;
  }

  @Watch('stationId')
  private onStationIdChanged() {
    this.debouncedFetchStationData();
  }

  private get streamUri(): string {
    if (this.token === '') {
      return `https://api.melos.info/stream/public/${this.stationId}`;
    }
    return `https://api.melos.info/stream/${this.stationId}`;
  }

  private goToLogin() {
    this.$router.push('/login');
  }

  private goToUserDetails() {
    this.$router.push('/me');
  }

  @Watch('connectedDevice')
  private onConnectedDeviceChanged() {
    this.$router.push('/home/stations');
  }

  private get connectedDevice(): string | undefined {
    return this.$store.state.connectedDevice;
  }

  @Watch('targetPlayerStatus')
  private onTargetPlayerStatusChanged() {
    if (this.connectedDevice === undefined) {
      return;
    }

    this.$melosControllerService.sendCommand(
      this.targetPlayerStatus === MelosPlayerStatus.PLAYING ? 'RESUME' : 'PAUSE',
      [],
    );
  }

  private get targetPlayerStatus(): MelosPlayerStatus {
    return this.$store.state.player?.targetStatus;
  }

  @Watch('targetElapsedTime')
  private onTargetElapsedTimeChanged() {
    if (this.connectedDevice === undefined) {
      return;
    }

    this.$melosControllerService.sendCommand(
      'SEEK',
      [String(Math.trunc(this.targetElapsedTime * 1000))],
    );
  }

  private get targetElapsedTime() {
    return this.$store.state.player?.targetElapsedTime;
  }

  @Watch('targetStationId')
  private onTargetStationIdChanged() {
    if (this.connectedDevice === undefined || this.targetStationId === this.lastSelectedStation) {
      return;
    }

    this.lastSelectedStation = this.targetStationId;

    this.$melosControllerService.sendCommand(
      'NEW_STATION_SELECTED',
      [this.targetStationId],
    );
  }

  private get targetStationId() {
    return this.$store.state.player?.stationId;
  }

  @Watch('targetTrack')
  private async onTargetTrackChanged() {
    return this.fetchStationData();
  }

  private get targetTrack() {
    return this.$store.state.player?.trackTitle;
  }

  @Watch('loop')
  private async onLoopChanged() {
    if (this.connectedDevice === undefined || this.targetStationId === this.lastSelectedStation) {
      return;
    }

    this.$melosControllerService.sendCommand(
      'SET_LOOP',
      [this.loop],
    );
  }

  private get loop() {
    return this.$store.state.player?.loop;
  }
  private async fetchStationData(): Promise<any> {
    return Promise.resolve()
    .then(() => {
      if (this.token !== '') {
        return this.fetchPrivateStationData();
      }
      return this.fetchPublicStationData();
    });
  }

  private async fetchPublicStationData(): Promise<any> {
    if (this.stationId === undefined) {
      return;
    }

    await this.$apiService.tunePublicStation(this.stationId);
    return Promise.all([
      this.fetchPublicWaveform(),
      this.fetchPublicStatus(),
    ]);
  }

  private async fetchPrivateStationData(): Promise<any> {
    if (this.stationId === undefined) {
      return;
    }

    await this.$apiService.tuneStation(this.stationId);
    return Promise.all([
      this.fetchWaveform(),
      this.fetchStatus(),
    ]);
  }

  private async fetchWaveform(): Promise<void> {
    if (this.stationId === undefined) {
      return;
    }
    return this.$apiService.getStationWaveform(this.stationId)
    .then((waveformContent: any) => {
      this.$store.commit('setPlayerData', { waveformContent });
    });
  }

  private async fetchPublicWaveform(): Promise<void> {
    if (this.stationId === undefined) {
      return;
    }
    return this.$apiService.getPublicStationWaveform(this.stationId)
    .then((waveformContent: any) => {
      this.$store.commit('setPlayerData', { waveformContent });
    });
  }

  private async fetchStatus(): Promise<void> {
    if (this.stationId === undefined) {
      return;
    }
    return this.$apiService.getStationStatus(this.stationId)
    .then((status: StationStatus) => {
      this.$store.commit('setPlayerData', {
        audioSource: `${this.streamUri}?t=${new Date().getTime()}`,
        targetElapsedTime: status.elapsedTime / 1000,
        trackTitle: status.currentStream,
        provider: status.provider,
        link: status.link,
        author: status.author,
        imageSrc: `${this.streamUri}/cover?access_token=${this.token}&t=${new Date().getTime()}`,
        trackDuration: status.duration,
      });
    });
  }

  private async fetchPublicStatus(): Promise<void> {
    if (this.stationId === undefined) {
      return;
    }
    return this.$apiService.getPublicStationStatus(this.stationId)
    .then((status: StationStatus) => {
      this.$store.commit('setPlayerData', {
        audioSource: `${this.streamUri}?t=${new Date().getTime()}`,
        targetElapsedTime: status.elapsedTime / 1000,
        trackTitle: status.currentStream,
        provider: status.provider,
        link: status.link,
        author: status.author,
        imageSrc: `${this.streamUri}/cover?t=${new Date().getTime()}`,
        trackDuration: status.duration,
      });
    });
  }

  private async fetchPublicStations(): Promise<void> {
    const melosStations = await this.$apiService.getPublicStations();
    this.$store.commit('setStations', (melosStations || []).sort((station1, station2) => {
      return station1.name < station2.name ? -1 : 1;
    }));
  }

  private async fetchStations(): Promise<void> {
    const melosStations: MelosStation[] = await this.$apiService.getStations();
    this.$store.commit('setStations', (melosStations || []).sort((station1, station2) => {
      return station1.name < station2.name ? -1 : 1;
    }));
  }

}
